import { defineStore } from 'pinia'
import { ref, onMounted, computed } from 'vue'
import {
  type SuperUserQuestionnaire as Questionnaire,
  type GenericDataMapper,
  QuestionnairesService,
  OrganisationsService,
  LawFieldsService,
  type PaginatedTOrganisationList as OrganisationsList,
  type LawFields,
  CategoryEnum,
  FieldTypeEnum,
  type QuestionnaireQuestion,
  type QuestionnaireSection,
  CasesService,
  TagRelationshipEnum,
  RelationshipTypeEnum
} from '@/types/generated'
import { type Option } from '@/components/admin/form/SelectField.vue'
import {
  EnvelopeIcon,
  HashtagIcon,
  CalendarIcon,
  LinkIcon,
  PhoneIcon,
  DocumentTextIcon,
  ArrowUpTrayIcon,
  QuestionMarkCircleIcon,
  ListBulletIcon,
  PencilSquareIcon,
  CheckCircleIcon
} from '@heroicons/vue/24/outline'
import { v4 as uuidv4 } from 'uuid'
import { TimerIcon } from 'lucide-vue-next'
import { entitiesDE } from '@/i18n/entity'

export const useQuestionnaireEditorStore = defineStore('questionnaireEditor', () => {
  const questionnaire = ref<Questionnaire | null>(null)

  const tags = ref<GenericDataMapper[]>([])
  const tagOptions = computed(() => {
    return tags.value.map((tag) => ({ value: tag.key, title: tag.label }))
  })

  const fieldTypes = Object.values(FieldTypeEnum).map((type) => ({ key: type }))
  const fieldTypeIcons: { [key in FieldTypeEnum]: Vue.component } = {
    [FieldTypeEnum.MULTISELECT]: ListBulletIcon,
    [FieldTypeEnum.SELECT]: CheckCircleIcon,
    [FieldTypeEnum.TEXTAREA]: DocumentTextIcon,
    [FieldTypeEnum.FILEFIELD]: ArrowUpTrayIcon,
    [FieldTypeEnum.MULTIFILEFIELD]: ArrowUpTrayIcon,
    [FieldTypeEnum.TIMEFIELD]: TimerIcon,
    [FieldTypeEnum.DATEFIELD]: CalendarIcon,
    [FieldTypeEnum.TEXTINPUT]: PencilSquareIcon,
    [FieldTypeEnum.NUMBERINPUT]: HashtagIcon,
    [FieldTypeEnum.DECIMALNUMBERINPUT]: HashtagIcon,
    [FieldTypeEnum.EMAILINPUT]: EnvelopeIcon,
    [FieldTypeEnum.PHONEINPUT]: PhoneIcon,
    [FieldTypeEnum.URLINPUT]: LinkIcon,
    [FieldTypeEnum.YESNO]: QuestionMarkCircleIcon
  }

  const fieldTypeLabels: { [key in FieldTypeEnum]: string } = {
    [FieldTypeEnum.MULTISELECT]: 'Multiple choice',
    [FieldTypeEnum.SELECT]: 'Single choice',
    [FieldTypeEnum.TEXTAREA]: 'Text area',
    [FieldTypeEnum.FILEFIELD]: 'Single file upload',
    [FieldTypeEnum.MULTIFILEFIELD]: 'Multiple file upload',
    [FieldTypeEnum.TIMEFIELD]: 'Time',
    [FieldTypeEnum.DATEFIELD]: 'Date',
    [FieldTypeEnum.TEXTINPUT]: 'Text input',
    [FieldTypeEnum.NUMBERINPUT]: 'Number input',
    [FieldTypeEnum.DECIMALNUMBERINPUT]: 'Decimal number input',
    [FieldTypeEnum.EMAILINPUT]: 'Email input',
    [FieldTypeEnum.PHONEINPUT]: 'Phone input',
    [FieldTypeEnum.URLINPUT]: 'URL input',
    [FieldTypeEnum.YESNO]: 'Yes/No'
  }
  const fieldTypesOptions = computed(() =>
    fieldTypes
      .map((fieldType) => ({
        value: fieldType.key,
        title: fieldTypeLabels[fieldType.key as FieldTypeEnum],
        icon: fieldTypeIcons[fieldType.key as FieldTypeEnum]
      }))
      .sort((a, b) => {
        if (!a.title) return -1
        if (!b.title) return 1
        return a.title.localeCompare(b.title)
      })
  )

  const relationshipLabels: Record<TagRelationshipEnum, string> = {
    ...Object.fromEntries(Object.entries(entitiesDE).map(([key, value]) => [key, value]))
  } as Record<TagRelationshipEnum, string>

  const relationshipOptions = computed(() =>
    Object.values(TagRelationshipEnum)
      .map((relationship) => ({
        value: relationship,
        title: relationshipLabels[relationship as TagRelationshipEnum]
      }))
      .sort((a, b) => {
        if (!a.title) return -1
        if (!b.title) return 1
        return a.title.localeCompare(b.title)
      })
  )

  const organisationsList = ref<OrganisationsList | null>(null)
  const organisations = computed(() => {
    return organisationsList.value?.results
  })
  const lawFields = ref<LawFields | null>(null)
  const lawFieldsOptions = computed(() => {
    return Object.keys(lawFields.value?.data || {}).map((key) => {
      return {
        value: key,
        title: key
      }
    })
  })
  const lawTopicOptions = computed<Option[]>(() => {
    if (!questionnaire.value || !questionnaire.value.law_field) {
      console.log('no law field, returning empty')
      return []
    }
    console.log('law field', questionnaire.value.law_field)

    return (
      lawFields.value?.data[questionnaire.value.law_field]?.map((topic) => ({
        value: topic,
        title: topic
      })) || []
    ).sort((a, b) => a.value.localeCompare(b.value))
  })
  const categoryOptions = ref<Option[]>([
    {
      value: CategoryEnum.ALLGEMEIN as string,
      title: CategoryEnum.ALLGEMEIN as string
    },
    {
      value: CategoryEnum.RECHTSSPEZIFISCH as string,
      title: CategoryEnum.RECHTSSPEZIFISCH as string
    }
  ])

  const toggleEnableAllCustomOrganisations = () => {
    if (isEnabledForAllCustomOrganisations.value) {
      console.log(
        'disabling all custom organisations',
        organisations.value?.length,
        questionnaire.value?.enabled_custom_orgs.length
      )
      questionnaire.value!.enabled_custom_orgs = []
    } else {
      console.log('enabling all custom organisations')
      questionnaire.value!.enabled_custom_orgs = organisations.value?.map((org) => org.uuid) || []
    }
  }

  const isEnabledForAllCustomOrganisations = computed(() => {
    return organisations.value?.length === questionnaire.value?.enabled_custom_orgs.length
  })

  const _synchronizeJumpTo = (question: QuestionnaireQuestion) => {
    /**
     * Synchronize jump_to fields to ensure that they have the same length as the choices array.
     */
    if (!question.choices) {
      question.choices = []
    }

    if (question.jump_to !== null && question.jump_to.length < question.choices.length) {
      const missingCount = question.choices.length - question.jump_to.length
      console.log('missing count', missingCount, question)
      for (let i = 0; i < missingCount; i++) {
        question.jump_to.push({ value: '', uuid: null })
      }
    }
    _updateJumpTo()
  }

  const _updateJumpTo = () => {
    /**
     * Update jump_to fields to ensure that they point to existing questions.
     * If a jump_to field points to a non-existing question, it will be set to null.
     */
    questionnaire.value?.schema.forEach((section) => {
      section.items.forEach((question) => {
        if (question.jump_to) {
          question.jump_to.forEach((jumpTo) => {
            if (jumpTo.uuid === null || jumpTo.uuid === 'END') {
              return
            }

            // Check if the target question exists in any section
            const targetExists = questionnaire.value?.schema.some((s) =>
              s.items.some((q) => q.uuid === jumpTo.uuid)
            )

            if (!targetExists) {
              jumpTo.uuid = null
            }
          })
        }
      })
    })
  }

  const copyQuestion = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    const newQuestion = JSON.parse(JSON.stringify(question))
    newQuestion.uuid = uuidv4()
    newQuestion.label += ' (copy)'
    const index = section.items.indexOf(question)
    section.items.splice(index + 1, 0, newQuestion)
    _synchronizeJumpTo(newQuestion)
    return newQuestion
  }

  const removeQuestion = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    const index = section.items.indexOf(question)
    section.items.splice(index, 1)
    _updateJumpTo()
  }

  const addQuestion = (section: QuestionnaireSection, index?: number) => {
    const question: QuestionnaireQuestion = {
      uuid: uuidv4(),
      label: `New question ${section.items.length + 1}`,
      help: '',
      tag: '',
      tag_relationship: null,
      tag_number: null,
      type: FieldTypeEnum.TEXTINPUT,
      optional: false,
      choices: [],
      value: '',
      jump_to: null,
      starts_ai_analysis: false
    }
    section.items.splice(index === undefined ? section.items.length : index, 0, question)
  }

  const removeSection = (section: QuestionnaireSection) => {
    const index = questionnaire.value?.schema.indexOf(section)
    if (index !== undefined && index > -1) {
      questionnaire.value?.schema.splice(index, 1)
    }
    _updateJumpTo()
  }

  const duplicateSection = (section: QuestionnaireSection) => {
    const newSection = JSON.parse(JSON.stringify(section))
    newSection.uuid = uuidv4()
    newSection.title += ' (copy)'

    // Generate new UUIDs for all questions in the section
    newSection.items.forEach((question: QuestionnaireQuestion) => {
      question.uuid = uuidv4()
    })

    const index = questionnaire.value?.schema.indexOf(section)
    if (index !== undefined && index > -1) {
      questionnaire.value?.schema.splice(index + 1, 0, newSection)
    }

    // Update jump_to references in the questionnaire
    _updateJumpTo()

    return newSection
  }

  const addSection = (index?: number) => {
    const section: QuestionnaireSection = {
      uuid: uuidv4(),
      title: `New section ${questionnaire.value!.schema.length + 1}`,
      items: []
    }
    questionnaire.value?.schema.splice(
      index === undefined ? questionnaire.value?.schema.length : index,
      0,
      section
    )
  }

  const addCustomOrganisation = (uuid: string) => {
    questionnaire.value?.enabled_custom_orgs.push(uuid)
  }

  const removeCustomOrganisation = (uuid: string) => {
    const index = questionnaire.value?.enabled_custom_orgs.indexOf(uuid)
    if (index !== undefined && index > -1) {
      questionnaire.value?.enabled_custom_orgs.splice(index, 1)
    }
  }

  const init = async (uuid: string) => {
    questionnaire.value = (await QuestionnairesService.questionnairesRetrieve({
      uuid: uuid
    })) as Questionnaire

    // Ensure all questions have properly synchronized jump_to arrays
    questionnaire.value.schema.forEach((section) => {
      section.items.forEach((question) => {
        _synchronizeJumpTo(question)
      })
    })

    tags.value = (await CasesService.casesGenericDatamappersList()).results as GenericDataMapper[]
    organisationsList.value = await OrganisationsService.organisationsList({ limit: 0 })
    lawFields.value = await LawFieldsService.lawFieldsRetrieve()
  }

  return {
    questionnaire,
    organisations,
    lawFieldsOptions,
    lawTopicOptions,
    categoryOptions,
    tags,
    tagOptions,
    fieldTypes,
    fieldTypesOptions,
    init,
    copyQuestion,
    removeQuestion,
    addQuestion,
    removeSection,
    duplicateSection,
    addSection,
    addCustomOrganisation,
    removeCustomOrganisation,
    toggleEnableAllCustomOrganisations,
    relationshipOptions,
    isEnabledForAllCustomOrganisations
  }
})
